import { apiClient } from './apiClient';
import type {
  ManageLicensesListInput,
  ManageLicenseListDto,
  BulkLicenseActionResultDto,
  ExtendLicenseDto,
  BulkLicenseIdsWithReasonDto,
  BulkExtendLicensesDto,
  LicenseActionHistoryDto,
  LicenseActiveSessionDto,
} from '../types/manageLicenses';

const BASE = '/api/app/licenses-extn';

export const licensesManageService = {
  list: async (input: ManageLicensesListInput): Promise<{ totalCount: number; items: ManageLicenseListDto[] }> => {
    const { data } = await apiClient.post<{ totalCount: number; items: ManageLicenseListDto[] }>(
      `${BASE}/manage-list`,
      input,
    );
    return data;
  },

  idsForFilter: async (input: ManageLicensesListInput, maxCount = 500): Promise<string[]> => {
    const { data } = await apiClient.post<string[]>(
      `${BASE}/ids-for-filter?maxCount=${maxCount}`,
      input,
    );
    return data;
  },

  extend: async (id: string, dto: ExtendLicenseDto): Promise<ManageLicenseListDto> => {
    const { data } = await apiClient.post<ManageLicenseListDto>(`${BASE}/${id}/extend`, dto);
    return data;
  },

  bulkRevoke: async (dto: BulkLicenseIdsWithReasonDto): Promise<BulkLicenseActionResultDto> => {
    const { data } = await apiClient.post<BulkLicenseActionResultDto>(`${BASE}/bulk-revoke`, dto);
    return data;
  },

  bulkSuspend: async (dto: BulkLicenseIdsWithReasonDto): Promise<BulkLicenseActionResultDto> => {
    const { data } = await apiClient.post<BulkLicenseActionResultDto>(`${BASE}/bulk-suspend`, dto);
    return data;
  },

  bulkReactivate: async (licenseIds: string[]): Promise<BulkLicenseActionResultDto> => {
    const { data } = await apiClient.post<BulkLicenseActionResultDto>(`${BASE}/bulk-reactivate`, licenseIds);
    return data;
  },

  bulkExtend: async (dto: BulkExtendLicensesDto): Promise<BulkLicenseActionResultDto> => {
    const { data } = await apiClient.post<BulkLicenseActionResultDto>(`${BASE}/bulk-extend`, dto);
    return data;
  },

  suspendAndTerminate: async (id: string, reason: string): Promise<ManageLicenseListDto> => {
    const { data } = await apiClient.post<ManageLicenseListDto>(
      `${BASE}/${id}/suspend-and-terminate`,
      { reason },
    );
    return data;
  },

  terminateSession: async (sessionId: string): Promise<void> => {
    await apiClient.post<void>(`${BASE}/terminate-session/${sessionId}`);
  },

  activeSessions: async (id: string): Promise<LicenseActiveSessionDto[]> => {
    const { data } = await apiClient.get<LicenseActiveSessionDto[]>(`${BASE}/${id}/active-sessions`);
    return data;
  },

  activityHistory: async (
    id: string,
    skipCount: number,
    maxResultCount: number,
  ): Promise<{ totalCount: number; items: LicenseActionHistoryDto[] }> => {
    const { data } = await apiClient.post<{ totalCount: number; items: LicenseActionHistoryDto[] }>(
      `${BASE}/${id}/activity-history`,
      { skipCount, maxResultCount },
    );
    return data;
  },

  exportExcel: async (input: ManageLicensesListInput): Promise<Blob> => {
    const { data } = await apiClient.post(`${BASE}/export-excel`, input, { responseType: 'blob' });
    return data as Blob;
  },
};
